import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { useReferenceState } from "lib/@hooks/use-reference"
import { retrieveSfg20Differences } from "routes/plan/plugins/review-differences/controller/retrieve-sfg20-differences"
import { Bound } from "lib/@components/binding/Bound"
import { ScheduleChanges } from "routes/plan/plugins/review-differences/schedule-changes"
import { Box, Stack, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { formatTime, formatTimeChange } from "lib/format-time"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { REVIEW_SFG20_CHANGES_ACCESS } from "library/hubspot/tiered-access-demands.mjs"
import { TieredAccessGate } from "minimals-template/components/guards/tiered-access-gate"
import { Secure } from "lib/authorization/secure"

export function ChangesFromCurrentSfg20({ prefix = "" }) {
    const { planId } = useBoundContext()
    const [addedSkip] = useBoundValue("addedSkip", 0)
    const [removedSkip] = useBoundValue("removedSkip", 0)
    const [changedSkip] = useBoundValue("changedSkip", 0)
    const [sort] = useReferenceState("task-sort", [], "task-sort")

    const [take] = useBoundValue("take", 10)

    const { added, removed, changed, total, formerMinutes, currentMinutes } =
        retrieveSfg20Differences.useResults.busy("Updating differences", {
            regimeId: `${prefix}${planId}`,
            take,
            sort,
            addedSkip,
            removedSkip,
            changedSkip,
        }) ?? {}

    const bodyText = [
        "Tailored Schedules may contain content that is different from the SFG20 Library Schedule.",
        "Upgrade to Professional to see all these changes in one list, review which tasks have been edited, added or removed. Contact your account manager for details.",
    ]

    return added ? (
        <Secure
            demand={REVIEW_SFG20_CHANGES_ACCESS}
            fallback={
                <TieredAccessGate
                    title="Variations from Library"
                    bodyText={bodyText}
                    logEvent={"review-sfg20-changes"}
                />
            }
        >
            <Bound
                added={added}
                removed={removed}
                changed={changed}
                total={total}
                take={take}
                isChangesFromLibrary={true}
            >
                <Box mt={2} />
                <Stack direction="row" sx={{ pt: 1 }} spacing={1} alignItems="center">
                    <Iconify icon="mdi:home-time-outline" sx={{ color: "primary.main", fontSize: 24 }} />
                    <Typography data-cy="variations-summary" variant="subtitle2" component="div">
                        Change in estimated duration of {formatTimeChange(currentMinutes - formerMinutes)}. Duration now
                        estimated at {formatTime(currentMinutes)}
                    </Typography>
                </Stack>

                <ScheduleChanges.Slot />
            </Bound>
        </Secure>
    ) : (
        <LoadingScreen description="Calculating Variance" />
    )
}
